import { useEffect, useMemo, useRef } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faBan } from "@fortawesome/pro-solid-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import slugify from "slugify";
import { twJoin } from "tailwind-merge";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { IconPicker } from "src/cases/common/IconPicker";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import {
  CALC_MARGIN_LEFT,
  CALC_MENUBAR_HALF_WIDTH,
} from "src/cases/templates/CaseTemplatesWrapper";
import { BlockConfigFormByKind } from "src/cases/templates/caseBlocks/BlockConfigFormByKind";
import { BlockKindSelect } from "src/cases/templates/caseBlocks/BlockKindSelect";
import { BLOCK_ICONS } from "src/cases/templates/caseBlocks/blockIcons";
import {
  useCaseBlocksConfigActions,
  useSelectedBlockId,
} from "src/cases/templates/caseBlocks/CaseBlocksConfigContext";
import {
  BLOCK_KIND_CONFIGS,
  createBlockConfigPayload,
} from "src/cases/templates/caseBlocks/useBlockInputOptions";
import { useCurrentStep } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import { Config1 } from "src/clients/case-management";
import { IconButton } from "src/design-system/Icon";

const useBlockFormIndex = (): number => {
  const selectedBlockId = useSelectedBlockId();
  const { watch } = useFormContext<CaseTemplateFormValues>();

  // Use watch here intentionally to avoid bugs related to deleted blocks
  const blocksConfigs = watch("attributes.case_blocks_configs");

  return useMemo(() => {
    if (!selectedBlockId) return -1;

    return blocksConfigs.findIndex(
      (block) => block._internalId === selectedBlockId,
    );
  }, [blocksConfigs, selectedBlockId]);
};

export const BlockConfigSidebar: React.FC = () => {
  const selectedBlockId = useSelectedBlockId();
  const { selectBlock } = useCaseBlocksConfigActions();
  const blockIndex = useBlockFormIndex();

  const onClose = () => selectBlock(null);

  useCloseBlockWhenNotOnConfigureBlocksStep({ onClose });

  if (blockIndex < 0) {
    return null;
  }

  return (
    <div
      className={twJoin(
        "absolute top-0 right-0 bottom-0 flex flex-none flex-col gap-y-4 border-gray-200 border-l bg-white px-4",
        `min-w-[22rem] max-w-[38rem]`,
      )}
      data-loc="block-config-sidebar"
      style={
        {
          width: `calc(${CALC_MARGIN_LEFT} + ${CALC_MENUBAR_HALF_WIDTH} - 1.25rem)`,
        } as React.CSSProperties
      }
    >
      <BlockConfigSidebarHeader onClose={onClose} />
      <div className="flex flex-1 flex-col gap-4 pb-4">
        {/* Do not remove the key property, it is used to force a re-render of the form when the block index changes */}
        <BlockBaseConfigForm
          key={`base-${blockIndex}-${selectedBlockId}`}
          blockIndex={blockIndex}
        />
        <BlockConfigFormByKind
          key={`by-kind-${blockIndex}-${selectedBlockId}`}
          blockIndex={blockIndex}
        />
      </div>
    </div>
  );
};

const BlockConfigSidebarHeader: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  return (
    <div className="flex h-[50px] items-center justify-end border-gray-200 border-b">
      <div className="flex flex-1 flex-col gap-2.5 pl-1 font-inter-semibold-13px text-gray-800">
        Block configuration
      </div>
      <IconButton
        aria-label="Close sidebar"
        dataLoc="block-config-sidebar-close"
        icon={faXmark}
        size="md"
        onClick={onClose}
      />
    </div>
  );
};

const useCloseBlockWhenNotOnConfigureBlocksStep = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const [currentStepId] = useCurrentStep();

  useEffect(() => {
    if (currentStepId !== "configure-case-blocks") {
      onClose();
    }
  }, [currentStepId, onClose]);
};

type BlockConfigFormProps = {
  blockIndex: number;
};

const BlockBaseConfigForm: React.FC<BlockConfigFormProps> = ({
  blockIndex,
}) => {
  const isPublished = useIsTemplateVersionPublished();
  const { register, control, formState, getFieldState, setValue, getValues } =
    useFormContext<CaseTemplateFormValues>();
  const { errors } = formState;
  const hasPermission = useCheckFieldPermission();
  const getPropertyIcon = useGetCasePropertyIcon();

  const fieldPath = `attributes.case_blocks_configs.${blockIndex}` as const;
  const blockErrors = errors.attributes?.case_blocks_configs?.[blockIndex];

  const canEditTitle = hasPermission("edit", `${fieldPath}.title`);
  const canEditKey = hasPermission("edit", `${fieldPath}.key`);
  const canEditIcon = hasPermission("edit", `${fieldPath}.icon`);
  const canEditKind = hasPermission("edit", `${fieldPath}.config.kind`);

  // Track if the key was initially empty (for new blocks)
  const wasKeyInitiallyEmpty = useRef(!getValues(`${fieldPath}.key`));

  const handleKindChange = (newKind: string) => {
    const blockKind = newKind as Config1["kind"];

    setValue(`${fieldPath}.config.kind` as const, blockKind);

    const config = BLOCK_KIND_CONFIGS[blockKind];
    let defaultValue = config?.defaultValue;

    if (!defaultValue) {
      const currentAttributes = getValues("attributes");
      if (currentAttributes) {
        const templateFormValues = {
          attributes: currentAttributes,
        } as CaseTemplateFormValues;
        const result = config?.buildOptions?.(
          templateFormValues,
          getPropertyIcon,
        );
        defaultValue = result?.defaultValue;
      }
    }

    if (defaultValue) {
      const configPayload = createBlockConfigPayload(blockKind, defaultValue);
      setValue(`${fieldPath}.config` as const, configPayload);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <Label required>Title</Label>
        <Input
          data-loc="block-config-title-input"
          disabled={!canEditTitle}
          errored={!!blockErrors?.title}
          placeholder="Block title"
          fullWidth
          {...register(`${fieldPath}.title`, {
            onChange: (event) => {
              const { isDirty } = getFieldState(`${fieldPath}.key`);
              if (!isDirty && !isPublished && wasKeyInitiallyEmpty.current) {
                setValue(
                  `${fieldPath}.key`,
                  slugify(event.target.value, {
                    lower: true,
                    strict: true,
                    replacement: "_",
                  }),
                  { shouldValidate: true },
                );
              }
            },
            deps: `${fieldPath}.key`,
          })}
        />
        {blockErrors?.title && (
          <ErrorHint>{blockErrors.title.message}</ErrorHint>
        )}
      </div>

      <div className="flex flex-col">
        <Label required>Key</Label>
        <Input
          data-loc="block-config-key-input"
          disabled={!canEditKey}
          errored={!!blockErrors?.key}
          placeholder="block_key"
          fullWidth
          monospaced
          {...register(`${fieldPath}.key`, {
            deps: `${fieldPath}.title`,
          })}
        />
        {blockErrors?.key && <ErrorHint>{blockErrors.key.message}</ErrorHint>}
      </div>

      <div>
        <Label required>Block type</Label>

        <div className="flex gap-x-3">
          <div className="flex flex-1 flex-col">
            <Controller
              control={control}
              name={`${fieldPath}.config.kind`}
              render={({ field }) => (
                <BlockKindSelect
                  dataLoc="block-kind-select"
                  disabled={!canEditKind}
                  value={field.value}
                  onChange={handleKindChange}
                />
              )}
            />
          </div>
          <div className="flex flex-col">
            <Controller
              control={control}
              name={`${fieldPath}.icon`}
              render={({ field }) => (
                <IconPicker
                  disabled={!canEditIcon}
                  options={BLOCK_ICONS}
                  placeholder={faBan}
                  size="sm"
                  value={field.value}
                  allowClear
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
